import * as THREE from 'three'
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js'

/**
 * Ink lines from the depth buffer.
 *
 * Inverted-hull outlines are the usual trick, but they crack at every hard
 * corner of an extruded building and cost a second draw of the whole town. This
 * is one fullscreen pass that outlines everything already in the scene —
 * buildings, terrain, glTF props, characters — with no per-mesh work.
 *
 * The detector runs on **inverse** depth rather than depth. A flat plane seen at
 * a grazing angle has an enormous depth gradient, which is why naive depth-edge
 * filters paint the ground with lines; 1/z is affine across any plane in screen
 * space, so its second derivative is zero there and non-zero exactly at
 * silhouettes and creases, which is what we want ink on.
 */

export const EdgeOutlineShader = {
  name: 'EdgeOutlineShader',
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    tDepth: { value: null as THREE.Texture | null },
    resolution: { value: new THREE.Vector2(1, 1) },
    cameraNear: { value: 0.1 },
    cameraFar: { value: 1000 },
    /** Sample offset in pixels. Above ~1.5 the line stops reading as ink. */
    thickness: { value: 1.15 },
    /** How far to pull an edge pixel toward the ink colour. */
    strength: { value: 0.82 },
    /** Curvature, relative to inverse depth, that counts as an edge. */
    threshold: { value: 0.018 },
    outlineColor: { value: new THREE.Color(0x1d1712) },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    #include <packing>

    uniform sampler2D tDiffuse;
    uniform sampler2D tDepth;
    uniform vec2 resolution;
    uniform float cameraNear;
    uniform float cameraFar;
    uniform float thickness;
    uniform float strength;
    uniform float threshold;
    uniform vec3 outlineColor;
    varying vec2 vUv;

    // 1 / viewZ. Affine across any plane, which is the whole trick.
    float inverseDepth(vec2 uv) {
      float fragCoordZ = texture2D(tDepth, uv).x;
      float viewZ = perspectiveDepthToViewZ(fragCoordZ, cameraNear, cameraFar);
      return 1.0 / max(-viewZ, cameraNear);
    }

    void main() {
      vec4 base = texture2D(tDiffuse, vUv);
      vec2 texel = thickness / resolution;

      float c = inverseDepth(vUv);
      float l = inverseDepth(vUv - vec2(texel.x, 0.0));
      float r = inverseDepth(vUv + vec2(texel.x, 0.0));
      float u = inverseDepth(vUv + vec2(0.0, texel.y));
      float d = inverseDepth(vUv - vec2(0.0, texel.y));

      float curvature = max(abs(l + r - 2.0 * c), abs(u + d - 2.0 * c));

      // Tolerance rides with the local scale so a kerb two metres away and a
      // ridge seven kilometres out are judged by the same relative standard.
      float tolerance = threshold * c;
      float edge = smoothstep(tolerance, tolerance * 4.0, curvature);

      gl_FragColor = vec4(mix(base.rgb, outlineColor, edge * strength), base.a);
    }
  `,
}

export interface EdgeOutline {
  pass: ShaderPass
  /** Depth target the scene must render into for the pass to see anything. */
  depthTexture: THREE.DepthTexture
  setSize: (width: number, height: number) => void
  dispose: () => void
}

export function createEdgeOutline(
  camera: THREE.PerspectiveCamera,
  width: number,
  height: number,
  options: { strength?: number; thickness?: number; threshold?: number } = {},
): EdgeOutline {
  const depthTexture = new THREE.DepthTexture(width, height)
  depthTexture.type = THREE.UnsignedIntType
  depthTexture.minFilter = THREE.NearestFilter
  depthTexture.magFilter = THREE.NearestFilter

  const pass = new ShaderPass(EdgeOutlineShader)
  pass.uniforms.tDepth.value = depthTexture
  pass.uniforms.resolution.value.set(width, height)
  pass.uniforms.cameraNear.value = camera.near
  pass.uniforms.cameraFar.value = camera.far
  if (options.strength !== undefined) pass.uniforms.strength.value = options.strength
  if (options.thickness !== undefined) pass.uniforms.thickness.value = options.thickness
  if (options.threshold !== undefined) pass.uniforms.threshold.value = options.threshold

  return {
    pass,
    depthTexture,
    setSize: (w, h) => {
      // The composer's own setSize reallocates the shared depth attachment;
      // this only has to keep the sampling resolution honest.
      depthTexture.image.width = w
      depthTexture.image.height = h
      pass.uniforms.resolution.value.set(w, h)
      pass.uniforms.cameraNear.value = camera.near
      pass.uniforms.cameraFar.value = camera.far
    },
    dispose: () => {
      depthTexture.dispose()
      pass.dispose?.()
    },
  }
}
